import { ALL_ELEMENTS, getEquipmentById, getWeaponById } from "@/data";
import type { ForceElement, ForceList, ForceModel, ValidationError } from "@/types/force";
import type { EquipmentAccess, TrainingTier, WeaponCategory } from "@/types/game-data";
import { calculateForceTotalCost } from "./points";

/**
 * Validate an entire force list. Returns all errors and warnings.
 * Pure function — no side effects.
 */
export function validateForce(force: ForceList): ValidationError[] {
	const errors: ValidationError[] = [];

	// 1. Point budget check
	const totalCost = calculateForceTotalCost(force);
	if (force.maxPoints > 0 && totalCost > force.maxPoints) {
		errors.push({
			type: "error",
			targetId: null,
			rule: "point-budget",
			message: `Force costs ${totalCost}r but budget is ${force.maxPoints}r (over by ${totalCost - force.maxPoints}r)`,
		});
	}

	// 2. Validate each element
	for (const element of force.elements) {
		errors.push(...validateElement(element, force.faction));
	}

	// 3. Check at least one element exists
	if (force.elements.length === 0) {
		errors.push({
			type: "warning",
			targetId: null,
			rule: "empty-force",
			message: "Force has no elements",
		});
	}

	return errors;
}

/** Validate a single element */
function validateElement(element: ForceElement, faction: string): ValidationError[] {
	const errors: ValidationError[] = [];
	const template = ALL_ELEMENTS.find((t) => t.id === element.templateId);

	if (!template) {
		errors.push({
			type: "error",
			targetId: element.id,
			rule: "invalid-template",
			message: `Unknown element template: ${element.templateId}`,
		});
		return errors;
	}

	// Check faction match
	if (template.faction !== faction) {
		errors.push({
			type: "error",
			targetId: element.id,
			rule: "faction-mismatch",
			message: `${template.name} belongs to ${template.faction}, not ${faction}`,
		});
	}

	// Check tier is allowed
	if (!template.allowedTiers.includes(element.tier)) {
		errors.push({
			type: "error",
			targetId: element.id,
			rule: "invalid-tier",
			message: `${template.name} does not allow ${element.tier} training tier`,
		});
	}

	// Check model count
	if (element.models.length < template.minModels) {
		errors.push({
			type: "error",
			targetId: element.id,
			rule: "min-models",
			message: `${template.name} requires at least ${template.minModels} model(s), has ${element.models.length}`,
		});
	}
	if (element.models.length > template.maxModels) {
		errors.push({
			type: "error",
			targetId: element.id,
			rule: "max-models",
			message: `${template.name} allows at most ${template.maxModels} model(s), has ${element.models.length}`,
		});
	}

	// Check commander presence
	const commanders = element.models.filter((m) => m.role === "commander");
	if (template.hasCommander && commanders.length === 0) {
		errors.push({
			type: "error",
			targetId: element.id,
			rule: "missing-commander",
			message: `${template.name} requires a Commander`,
		});
	}
	if (commanders.length > 1) {
		errors.push({
			type: "error",
			targetId: element.id,
			rule: "too-many-commanders",
			message: `${template.name} can only have 1 Commander`,
		});
	}

	// Check sergeant count
	const sergeants = element.models.filter((m) => m.role === "sergeant");
	if (sergeants.length > 1) {
		errors.push({
			type: "error",
			targetId: element.id,
			rule: "too-many-sergeants",
			message: `${template.name} can only have 1 Sergeant`,
		});
	}
	if (sergeants.length > 0 && !template.allowSergeant) {
		errors.push({
			type: "error",
			targetId: element.id,
			rule: "sergeant-not-allowed",
			message: `${template.name} does not allow Sergeant upgrade`,
		});
	}

	// Check specialist weapon ratio
	const specialistModels = element.models.filter((m) =>
		m.weapons.some((wId) => {
			const w = getWeaponById(wId);
			return w && w.slot === "specialist";
		}),
	);
	if (template.specialistWeaponRatio > 0) {
		const maxSpecialists = Math.floor(element.models.length / template.specialistWeaponRatio);
		if (specialistModels.length > maxSpecialists) {
			errors.push({
				type: "error",
				targetId: element.id,
				rule: "specialist-weapon-ratio",
				message: `${template.name}: only ${maxSpecialists} of ${element.models.length} models may carry specialist weapons (1 in ${template.specialistWeaponRatio}), but ${specialistModels.length} do`,
			});
		}
	}

	// Validate each model's equipment access
	for (const model of element.models) {
		errors.push(...validateModelEquipment(model, element, template.equipmentAccess, element.tier));
		errors.push(
			...validateModelWeaponCategories(model, element, template.specialistWeaponCategories),
		);
	}

	return errors;
}

/** Validate that a model's equipment is allowed for the element */
function validateModelEquipment(
	model: ForceModel,
	element: ForceElement,
	allowedAccess: readonly EquipmentAccess[],
	tier: TrainingTier,
): ValidationError[] {
	const errors: ValidationError[] = [];

	for (const equipId of model.equipment) {
		const equip = getEquipmentById(equipId);
		if (!equip) {
			errors.push({
				type: "warning",
				targetId: element.id,
				modelId: model.id,
				rule: "unknown-equipment",
				message: `Unknown equipment: ${equipId}`,
			});
			continue;
		}

		// Check access level
		if (!allowedAccess.includes(equip.access)) {
			errors.push({
				type: "error",
				targetId: element.id,
				modelId: model.id,
				rule: "equipment-access",
				message: `${equip.name} requires ${equip.access} access, which this element does not have`,
			});
		}

		// Check tier-specific equipment
		if (equip.access === "tier1and2" && tier !== "tier1" && tier !== "tier2") {
			errors.push({
				type: "error",
				targetId: element.id,
				modelId: model.id,
				rule: "tier-equipment",
				message: `${equip.name} is only available to Tier 1 and Tier 2 operators`,
			});
		}
	}

	return errors;
}

/** Validate that a model's specialist weapons are in the allowed categories */
function validateModelWeaponCategories(
	model: ForceModel,
	element: ForceElement,
	allowedCategories: readonly WeaponCategory[],
): ValidationError[] {
	const errors: ValidationError[] = [];

	for (const weaponId of model.weapons) {
		const weapon = getWeaponById(weaponId);
		if (!weapon) continue;

		if (weapon.slot === "specialist" && !allowedCategories.includes(weapon.category)) {
			errors.push({
				type: "error",
				targetId: element.id,
				modelId: model.id,
				rule: "weapon-category",
				message: `${weapon.name} (${weapon.category}) is not available in this element type`,
			});
		}
	}

	return errors;
}
